import {
    Box,
    Button,
    Dialog,
    Stack,
    ThemeProvider,
    Typography,
} from '@mui/material'
import { lightTheme } from '../../theme'
import React, { useState, useEffect, useRef } from 'react'
import { StyledPaper } from '../../components/StyledPaper'
import { StringField } from '../../components/StringField'
import { useLoading } from '../../context/loading_context'
import { useSnack } from '../../context/snack_context'
import {
    FaceImageRequiredError,
    TwoFactorCodeRequiredError,
    doLogin,
} from '../../services/auth'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FaceCameraModal } from '../../components/FaceCameraModal'
import { useTranslation } from 'react-i18next'

interface Props {
    open: boolean
    handleClose(): void
}

export const LoginModal = ({ open, handleClose }: Props) => {
    const { t, i18n } = useTranslation()
    const loading = useLoading()
    const snack = useSnack()
    const [company, setCompany] = useState('')
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [twoFactorCode, setTwoFactorCode] = useState('')
    const [searchParams] = useSearchParams()
    const [isCloud, setIsCloud] = useState(false)
    const companyRef = useRef<HTMLInputElement | null>(null)
    const userRef = useRef<HTMLInputElement | null>(null)
    const passRef = useRef<HTMLInputElement | null>(null)
    const codeRef = useRef<HTMLInputElement | null>(null)
    const confirmRef = useRef<HTMLButtonElement | null>(null)
    const [openFaceDetectionModal, setOpenFaceDetectionModal] = useState(false)
    const [twoFactorRequired, setTwoFactorRequired] = useState(false)

    const keyboardHandler = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            confirmRef.current?.click()
        }
    }

    useEffect(() => {
        if (!open) return

        i18n.changeLanguage(navigator.language.split('-')[0])

        const isCloud = process.env.REACT_APP_MODE === 'cloud'
        setIsCloud(isCloud)

        setTimeout(() => {
            if (isCloud) companyRef.current?.focus()
            else userRef.current?.focus()
        }, 100)

        document.body.addEventListener('keypress', keyboardHandler)

        const expired = searchParams.get('expired')
        if (expired === 'true') snack.alert('Sua sessão expirou.')

        return () => {
            document.body.removeEventListener('keypress', keyboardHandler)
        }
    }, [open])

    const login = async (image?: string) => {
        loading.show()
        try {
            await doLogin({
                username: username,
                senha: password,
                login_image: image,
                codigo_2fa: twoFactorCode || undefined,
                company: isCloud ? company : undefined,
            })
            handleClose()
            window.location.href = `${window.origin}/system`
        } catch (error) {
            if (error instanceof FaceImageRequiredError) {
                setOpenFaceDetectionModal(true)
                snack.alert('Validação facial necessária')
            } else if (error instanceof TwoFactorCodeRequiredError) {
                setTwoFactorRequired(true)
                snack.alert('Código de verificação em duas etapas necessário')
                setTimeout(() => codeRef.current?.focus(), 100)
            } else {
                snack.connectionFail(error)
            }
        }
        loading.hide()
    }

    return (
        <ThemeProvider theme={lightTheme}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="xs"
                fullWidth
                PaperProps={{ className: 'force-light' }}
            >
                {openFaceDetectionModal && (
                    <FaceCameraModal
                        open={openFaceDetectionModal}
                        handleClose={setOpenFaceDetectionModal}
                        returnImage={(image) => {
                            login(image.content)
                            setOpenFaceDetectionModal(false)
                        }}
                        type="login"
                    />
                )}
                <Stack p={'20px'} alignItems={'center'}>
                    <Box
                        display={'flex'}
                        justifyContent={'center'}
                        sx={{
                            width: '100%',
                            p: '15px',
                        }}
                    >
                        <Box
                            component={'img'}
                            src="/frsistema-logo.png"
                            sx={{ maxWidth: '70%' }}
                        />
                    </Box>
                    <Typography mt={'10px'} fontWeight={'bold'}>
                        {t('Accede a tu cuenta')}
                    </Typography>
                    <Typography
                        fontSize={'14px'}
                        fontWeight={'500'}
                        mb={'10px'}
                        textAlign={'center'}
                    >
                        {isCloud
                            ? t(
                                  'Para acceder introduzca su empresa, usuario y contraseña a continuación:'
                              )
                            : t(
                                  'Para acceder, introduce tu nombre de usuario y contraseña a continuación:'
                              )}
                    </Typography>
                    {isCloud && (
                        <StringField
                            value={company}
                            setValue={setCompany}
                            label="Empresa"
                            maxLength={50}
                            sx={{
                                mt: '10px',
                                width: { xs: '100%' },
                            }}
                            ref={companyRef}
                            size="small"
                        />
                    )}
                    <StringField
                        value={username}
                        setValue={setUsername}
                        label="Usuario"
                        maxLength={50}
                        sx={{ mt: '10px', width: { xs: '100%' } }}
                        ref={userRef}
                        size="small"
                    />
                    <StringField
                        value={password}
                        setValue={setPassword}
                        label="Contraseña"
                        maxLength={50}
                        sx={{ mt: '10px', width: { xs: '100%' } }}
                        type="password"
                        ref={passRef}
                        size="small"
                    />
                    {twoFactorRequired && (
                        <StringField
                            value={twoFactorCode}
                            setValue={setTwoFactorCode}
                            label="Código de verificación"
                            maxLength={10}
                            sx={{ mt: '10px', width: { xs: '100%' } }}
                            ref={codeRef}
                            size="small"
                        />
                    )}
                    <Button
                        variant="contained"
                        sx={{
                            textTransform: 'none',
                            width: { xs: '100%' },
                            fontSize: '18px',
                            p: '6px',
                            mt: '25px',
                        }}
                        color="primary"
                        onClick={() => login()}
                        disabled={
                            !username ||
                            !password ||
                            (isCloud && !company) ||
                            (twoFactorRequired && !twoFactorCode)
                        }
                        ref={confirmRef}
                    >
                        {t('Entrar')}
                    </Button>
                </Stack>
            </Dialog>
        </ThemeProvider>
    )
}
